// src/components/common/Sidebar.jsx
import { useState, useEffect } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';

const Sidebar = () => {
  const { user, logout, loading, getAuthenticatedApi } = useAuth();
  const navigate = useNavigate();
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);

  // Fetch profile picture when user is available
  useEffect(() => {
    const fetchProfilePicture = async () => {
      if (user && getAuthenticatedApi) {
        try {
          const api = getAuthenticatedApi();
          const response = await api.get('/profile/me');
          
          if (response.data?.profile_pic) {
            const fullImageUrl = response.data.profile_pic.startsWith('http') 
              ? response.data.profile_pic 
              : `https://app.writeapp.in${response.data.profile_pic}`;
            setProfilePicture(fullImageUrl);
          }
        } catch (error) {
          console.error('Error fetching profile picture:', error);
        }
      }
    };

    fetchProfilePicture();
  }, [user, getAuthenticatedApi]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuOpen && !event.target.closest('.profile-menu-container')) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileMenuOpen]);

  // Adjust body padding based on sidebar state & screen width
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const updatePadding = () => {
      if (mediaQuery.matches) {
        const width = sidebarExpanded ? '16rem' : '4rem';
        document.body.style.paddingLeft = width;
      } else {
        document.body.style.paddingLeft = '0px';
      }
    };
    updatePadding();
    mediaQuery.addEventListener('change', updatePadding);
    return () => {
      document.body.style.paddingLeft = '';
      mediaQuery.removeEventListener('change', updatePadding);
    };
  }, [sidebarExpanded]);

  // Sidebar animation variants
  const sidebarVariants = {
    expanded: {
      width: '16rem',
      transition: { type: 'spring', stiffness: 100, damping: 20 },
    },
    collapsed: {
      width: '4rem',
      transition: { type: 'spring', stiffness: 100, damping: 20 },
    },
  };

  const handleLogout = () => {
    logout();
    setProfileMenuOpen(false);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        Loading...
      </div>
    );
  }

  return (
    <motion.aside
      variants={sidebarVariants}
      animate={sidebarExpanded ? 'expanded' : 'collapsed'}
      className="h-screen bg-[#111] border-r border-gray-800 fixed left-0 top-0 z-40 hidden md:flex flex-col justify-between"
    >
      <div>
        <button
          onClick={() => setSidebarExpanded((prev) => !prev)}
          className="p-2 m-2 rounded-full hover:bg-gray-800 transition-colors duration-200"
        >
          {sidebarExpanded ? '←' : '→'}
        </button>

        <div className={`mt-8 ${sidebarExpanded ? 'px-4' : 'px-0'}`}>
          <AnimatePresence>
            {sidebarExpanded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="block mb-6"
              >
                <Link to="/">
                  <h1 className="text-xl font-bold text-white">WriteApp</h1>
                </Link>
              </motion.div>
            )}
          </AnimatePresence>

          <nav className="flex flex-col space-y-2">
            {[
              {
                to: '/forum',
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                ),
                label: 'Home',
              },
              {
                to: '/blog',
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1M19 20a2 2 0 002-2V8a2 2 0 00-2-2h-5a2 2 0 00-2 2v10a2 2 0 002 2h5z"
                    />
                  </svg>
                ),
                label: 'Blogs',
              },
              {
                to: '/about',
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ),
                label: 'About',
              },
            ].map(({ to, icon, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `group relative flex items-center px-4 py-2 ${
                    isActive ? 'bg-gray-800 text-white' : 'text-gray-300 hover:bg-gray-800'
                  } rounded transition-colors duration-200`
                }
              >
                {icon}
                <AnimatePresence>
                  {sidebarExpanded && (
                    <motion.span
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                      className="ml-3"
                    >
                      {label}
                    </motion.span>
                  )}
                </AnimatePresence>
                {!sidebarExpanded && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 rounded-md text-sm invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200">
                    {label}
                  </div>
                )}
              </NavLink>
            ))}

            {/* Admin Panel button, only visible to admins */}
            {user?.role === 'ADMIN' && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  `group relative flex items-center px-4 py-2 ${
                    isActive ? 'bg-gray-800 text-white' : 'text-gray-300 hover:bg-gray-800'
                  } rounded transition-colors duration-200`
                }
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 3h18v4H3V3zm0 6h18v4H3V9zm0 6h18v4H3v-4z"
                  />
                </svg>
                <AnimatePresence>
                  {sidebarExpanded && (
                    <motion.span
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                      className="ml-3"
                    >
                      Admin Panel
                    </motion.span>
                  )}
                </AnimatePresence>
                {!sidebarExpanded && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 rounded-md text-sm invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200">
                    Admin Panel
                  </div>
                )}
              </NavLink>
            )}
          </nav>
        </div>
      </div>

      {user && (
        <div className={`mb-4 ${sidebarExpanded ? 'px-4' : 'px-2'} profile-menu-container`}>
          <div
            className="flex items-center cursor-pointer p-2 rounded-lg hover:bg-gray-800 transition-colors"
            onClick={() => setProfileMenuOpen((prev) => !prev)}
          >
            <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center overflow-hidden relative">
              {profilePicture ? (
                <img
                  src={profilePicture}
                  alt={user.name || 'Profile'}
                  className="w-full h-full object-cover"
                  onError={() => setProfilePicture(null)} // Fallback to letter if image fails
                />
              ) : (
                <span className="text-white text-sm font-medium">
                  {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                </span>
              )}
            </div>
            {sidebarExpanded && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
                className="ml-3 flex-grow"
              >
                <p className="text-sm font-medium text-white truncate">{user.name}</p>
              </motion.div>
            )}
          </div>
          <AnimatePresence>
            {profileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className={`mt-2 py-2 bg-gray-800 rounded-lg shadow-lg ${
                  sidebarExpanded ? 'w-full' : 'absolute left-full bottom-0 mb-12 ml-2 w-40'
                }`}
              >
                <Link
                  to="/profile/me"
                  className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                  onClick={() => setProfileMenuOpen(false)}
                >
                  Your Profile
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                >
                  Logout
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.aside>
  );
};

export default Sidebar;